import {
  EXPLICIT_UNKNOWN_SOURCE_DISPLAY,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PropertyData,
  PropertyDataWithOptionalReadonly,
  SourceType,
  SubjectData,
} from '@aloft/toolkit-shared';
import { IconName, ProductColor } from '@aloft/toolkit-sui';

import { startCase } from 'lodash';

import { setPropertyData } from '../../redux/features/propertyDataSlice.ts';
import { AppDispatch, PropertyDataState } from '../../redux/types.ts';
import { calcSimilarity } from '../RecommendedCompSelection/internal/similarity/calcSimilarity.ts';
import { CompSelectionProperty } from '../RecommendedCompSelection/types.ts';
import { compsToCompSelectionProps } from '../RecommendedCompSelection/utils/compsToCompSelectionProps.ts';
import { ADJUSTMENTS_GRID_DATA_SOURCES, FEATURE_GRID_DATA_SOURCES } from './consts.ts';
import {
  PropertyGridColumnDefinition,
  PropertyGridColumnType,
  PropertyGridForm,
  PropertyGridType,
} from './types.ts';

export const getDataSourcesForGridType = (gridType: PropertyGridType): SourceType[] => {
  switch (gridType) {
    case 'FeatureGrid':
      return FEATURE_GRID_DATA_SOURCES;
    case 'AdjustmentsGrid':
      return ADJUSTMENTS_GRID_DATA_SOURCES;
    case 'SensitivityGrid':
    default:
      return [];
  }
};

/**
 * Returns the background color for the property grid based on the active AIV/ARV tab.
 */
export const getTabBackgroundColor = (activeTab?: string): ProductColor => {
  if (activeTab === 'aiv') return 'aivLight';
  if (activeTab === 'arv') return 'arvLight';
  return 'surface6';
};

export const getDefaultFormColumns = (
  compSequence: string[],
  dataSources: SourceType[],
): PropertyGridColumnDefinition[] =>
  [PROPERTY_GRID_SUBJECT_PROPERTY, ...compSequence].flatMap((propertyId) => [
    {
      propertyId,
      type: 'property',
    },
    ...dataSources.map((source) => ({
      propertyId,
      type: source,
    })),
  ]);

export const getDataSourceDisplayInfo = (
  dataSource: PropertyGridColumnType,
): { icon: IconName; text: string } => {
  switch (dataSource) {
    case 'abode_mls':
      return {
        icon: 'mls-listing',
        text: 'MLS',
      };
    case 'abode_public_record':
      return {
        icon: 'public-record',
        text: 'Public Record',
      };
    case 'inspectify_inspection':
      return {
        icon: 'inspectifyReport',
        text: 'Inspection',
      };
    case 'xml':
      return { icon: 'file-xml', text: 'XML Data' };
    case 'regression':
      return { icon: 'line-chart', text: 'Regression' };
    case 'sensitivity':
      return { icon: 'speedometer', text: 'Sensitivity' };
    case 'unknown':
      return { icon: 'question', text: EXPLICIT_UNKNOWN_SOURCE_DISPLAY };
    case 'estimated':
      return { icon: 'estimated', text: 'Estimated' };
    default:
      return { icon: 'user-input', text: 'Unknown Data Source' };
  }
};

export const getSelectSourceTooltip = (sourceType: SourceType) => {
  switch (sourceType) {
    case 'abode_mls':
      return 'MLS';
    case 'abode_public_record':
      return 'Public Records';
    case 'inspectify_inspection':
      return 'Inspection';
    case 'unknown':
      return EXPLICIT_UNKNOWN_SOURCE_DISPLAY;
    case 'estimated':
      return 'Estimated';
    default:
      return startCase(sourceType);
  }
};

const setDefaultCraftsmanQualityWhenMissing = (
  propertyData: PropertyDataWithOptionalReadonly | SubjectData,
  propertyId: string,
) => {
  const improvements = propertyData.editableData?.improvements;
  const arvImprovements =
    propertyId === 'subject' ? (propertyData as SubjectData).editableData?.arv?.improvements : null;
  if (arvImprovements && !arvImprovements.craftsmanQuality) {
    arvImprovements.craftsmanQuality = (
      propertyData as SubjectData
    ).editableData.arv?.improvements.uadQuality;
  }
  if (improvements && !improvements.craftsmanQuality) {
    improvements.craftsmanQuality = improvements.uadQuality;
  }
};

const setSubjectArvImprovements = (propertyData: SubjectData) => {
  const { arv, improvements } = propertyData.editableData ?? {};
  const arvImprovements = arv?.improvements;
  if (!improvements || !arvImprovements) return;

  arvImprovements.propertyType = improvements.propertyType;
  arvImprovements.otherAdjustmentsDisplayName = improvements.otherAdjustmentsDisplayName;
};

// We call this function in each field to avoid an erroneous field preventing updating the other fields
export const handleUpdatePropertyData = (
  propertyId: string,
  formValues: PropertyGridForm,
  dispatch: AppDispatch,
  propertyData?: PropertyDataWithOptionalReadonly | SubjectData,
) => {
  if (!propertyData) {
    console.warn('handleUpdatePropertyData called with no propertyData');
    return;
  }

  let propertyDataState: PropertyDataState;

  // eslint-disable-next-line no-param-reassign
  if (!propertyData.readonlyData) propertyData.readonlyData = {};

  setDefaultCraftsmanQualityWhenMissing(propertyData, propertyId);

  if (propertyId === PROPERTY_GRID_SUBJECT_PROPERTY) {
    setSubjectArvImprovements(propertyData as SubjectData);

    propertyDataState = {
      id: propertyId,
      subjectData: propertyData as SubjectData,
    };
  } else {
    propertyDataState = {
      id: propertyId,
      compData: propertyData as PropertyData,
    };
  }

  dispatch(setPropertyData(propertyDataState));
};

export const getSimilarityScore = (
  subjectCompSelectionProperty: CompSelectionProperty,
  propertyId: string,
  propertyData: PropertyData,
) => {
  const compSelectionProperty = compsToCompSelectionProps(
    [propertyId],
    { [propertyId]: propertyData },
    subjectCompSelectionProperty,
  )[0];

  if (!compSelectionProperty) return '-';
  return calcSimilarity(subjectCompSelectionProperty, compSelectionProperty).toFixed(0);
};
